import { selectLocalStore, type LocalStorePort } from '../../platform/local-store';

/**
 * WHICH CONVERSATION AM I CONTINUING? — the active Sol conversation pointer.
 * M11 / I-065.
 *
 * ---------------------------------------------------------------------------
 * THE REAL-PHONE DEFECT
 * ---------------------------------------------------------------------------
 *
 * Brandon discusses CAP-0002, and *"What status is it now?"* correctly means
 * CAP-0002. He swipes Crewlink away, reopens it, asks the same words — and Sol
 * answers about the Project instead. **The conversation was never lost.**
 * Migration `0021` stored it, `0022` guards its context, and RLS keeps it his.
 * What died with the process was the one thing that lived only in React state:
 * **the id of the conversation the app was in.**
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS, AND — MORE IMPORTANTLY — WHAT IT IS NOT
 * ---------------------------------------------------------------------------
 *
 * It is ONE server-issued identifier, scoped to one owner and one Project.
 * That is the entire record. It exists to answer a single question when the app
 * starts again: *which conversation am I continuing?*
 *
 * **IT IS NOT AUTHORITY AND IT IS NOT A CREDENTIAL.** Holding an id grants
 * nothing: the server loads a conversation **as the caller**, so an id that is
 * not theirs simply does not come back, and the turn begins a fresh one. That
 * refusal already exists in `sol-ask.ts` and is not weakened here. Nothing in
 * this file is ever presented as proof of anything.
 *
 * **IT IS NOT CONVERSATIONAL MEMORY.** No message, no answer, no Capture or
 * Project fact, no status, no filter, no cursor, no evidence, no source record,
 * no proposal or ticket, no provider response, no reasoning, no secret. Those
 * belong to the server, and a copy on the device would be a stale second source
 * of truth — the exact failure that recovery must not introduce.
 * **CONVERSATION CONTEXT RESOLVES MEANING; CREWLINK RECORDS PROVIDE FACTS**,
 * and every factual answer still comes from a live, authorized read.
 *
 * ---------------------------------------------------------------------------
 * WHY `LocalStorePort` AND NOT A NEW MECHANISM
 * ---------------------------------------------------------------------------
 *
 * The ports layer already has exactly the right port. `LocalStorePort` is
 * device-local, app-private, IndexedDB-backed durable storage for **product**
 * state (M4 / S-04-01), and it is explicitly NOT for auth material — which is
 * `SessionStoragePort`'s job under D-005. A conversation id is product state
 * and is not auth material, so this needs **no new platform decision, no new
 * dependency and no new security policy**: it reuses the mechanism Capture
 * drafts, recordings and staged photos already use, including its degraded
 * behaviour. If the store cannot be opened at all, every operation is a safe
 * no-op and recovery is simply unavailable — the product loses continuity,
 * never correctness.
 *
 * ---------------------------------------------------------------------------
 * THE THREE BOUNDARIES, UNCHANGED FROM THE VISIT ITSELF
 * ---------------------------------------------------------------------------
 *
 * **The owner** — the key is per user and the record names its owner, so a
 * record belonging to somebody else is refused even if it were planted under
 * another key. **The Project** — a different Project is a different visit, so a
 * pointer for Project A is not offered under Project B. **Start over** — the
 * deliberate end of a conversation drops the pointer, which is the whole of
 * what "continue no longer" means. It is NOT "delete history": the stored
 * conversation is untouched, and nothing here can delete one.
 *
 * A deliberate sign-out drops it too, alongside the Capture draft, the pending
 * recording and staged photos (D-019, D-021, D-027) — handing the phone to
 * someone else is what that button is for.
 */

/** Every pointer key starts here. One record per owner, and only ever one. */
export const SOL_CONVERSATION_POINTER_KEY_PREFIX = 'sol-conversation:';

export function solConversationPointerKey(ownerUserId: string): string {
  return `${SOL_CONVERSATION_POINTER_KEY_PREFIX}${ownerUserId}`;
}

/**
 * The stored envelope. **Version 1 is the whole shape, and it is this small on
 * purpose** — every field here has to earn its place, because each one is a
 * chance for the device to disagree with the server.
 */
interface SolConversationPointerRecord {
  readonly version: 1;
  readonly ownerUserId: string;
  /** The Project the conversation belongs to. Null for a future global Sol. */
  readonly projectId: string | null;
  readonly conversationId: string;
}

const VERSION = 1;

export interface SolConversationPointerStore {
  /**
   * The conversation to continue for this owner under this Project, or null.
   *
   * **NULL IS ALWAYS SAFE** — it means "begin a new conversation", which is
   * what the app did before this file existed.
   */
  read(ownerUserId: string, projectId: string | null): Promise<string | null>;
  /** Record the conversation the SERVER put this turn in. Never one we minted. */
  remember(input: {
    readonly ownerUserId: string;
    readonly projectId: string | null;
    readonly conversationId: string;
  }): Promise<void>;
  /** Start over, and sign-out. Drops the pointer; deletes no conversation. */
  forget(ownerUserId: string): Promise<void>;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Read the envelope, or refuse it.
 *
 * **A REFUSAL IS NOT AN ERROR** — it returns null and the caller begins a new
 * conversation. Nothing is repaired, and nothing partially read is used: a
 * record from a future version is left on disk untouched rather than deleted,
 * exactly as the Capture draft envelope treats one.
 */
function parsePointer(
  raw: unknown,
  expectedOwnerUserId: string,
  expectedProjectId: string | null,
): string | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  if (record.version !== VERSION) return null;
  if (!isNonEmptyString(record.ownerUserId)) return null;
  // **THE OWNER CHECK IS THE POINT.** The key is already per-user; this refuses
  // a record that NAMES somebody else whatever key it was found under.
  if (record.ownerUserId !== expectedOwnerUserId) return null;
  if (record.projectId !== null && !isNonEmptyString(record.projectId)) return null;
  if (record.projectId !== expectedProjectId) return null;
  if (!isNonEmptyString(record.conversationId)) return null;
  return record.conversationId;
}

export function createSolConversationPointerStore(
  store: LocalStorePort,
): SolConversationPointerStore {
  return {
    async read(ownerUserId, projectId) {
      // A signed-out app has no owner, and therefore nothing to recover. This
      // is what makes "signed out recovers nothing" true by construction rather
      // than by a caller remembering to check.
      if (!isNonEmptyString(ownerUserId)) return null;
      const raw = await store.get(solConversationPointerKey(ownerUserId)).catch(() => null);
      return parsePointer(raw, ownerUserId, projectId);
    },

    async remember({ ownerUserId, projectId, conversationId }) {
      if (!isNonEmptyString(ownerUserId)) return;
      if (!isNonEmptyString(conversationId)) return;
      const record: SolConversationPointerRecord = {
        version: VERSION,
        ownerUserId,
        projectId,
        conversationId,
      };
      // **THE RECORD IS REPLACED, NEVER MERGED.** One owner has one active
      // conversation pointer; a merge would let a Project from three visits ago
      // survive into a request it has nothing to do with.
      await store.set(solConversationPointerKey(ownerUserId), record).catch(() => undefined);
    },

    async forget(ownerUserId) {
      if (!isNonEmptyString(ownerUserId)) return;
      await store.remove(solConversationPointerKey(ownerUserId)).catch(() => undefined);
    },
  };
}

/**
 * The application's store, created once at first use.
 *
 * Lazy rather than at module load, for the same reason the Capture draft store
 * is: importing this file — which the auth feature does, for the sign-out purge
 * — must never open a database as a side effect of an import.
 */
let applicationStore: SolConversationPointerStore | null = null;

export function getSolConversationPointerStore(): SolConversationPointerStore {
  applicationStore ??= createSolConversationPointerStore(selectLocalStore());
  return applicationStore;
}
